import { type PropsWithChildren, createElement, forwardRef } from 'react';

import {
  alignSelfVars,
  breakpointVariants,
  gridAreaVars,
  gridColumnEndVars,
  gridColumnStartVars,
  gridColumnVars,
  gridRowEndVars,
  gridRowStartVars,
  gridRowVars,
  justifySelfVars,
  orderVars,
} from './GridItem.css';
import { DynamicVariantProp } from '../../types';
import { getClassNames } from '../../util/css';
import { extractDynamicStyles } from '../../util/css/vanilla-extract';

const sparkyClassName = 'sparky-grid-item';

export const GridItem = forwardRef<HTMLDivElement, PropsWithChildren<GridItemProps>>(
  (
    {
      children,
      as = 'div',
      className = '',
      gridArea,
      gridColumnStart,
      gridColumnEnd,
      gridColumn,
      gridRowStart,
      gridRowEnd,
      gridRow,
      order,
      alignSelf,
      justifySelf,
    },
    ref,
  ) => {
    const { classNames: dynamicCssClasses, cssVars } = extractDynamicStyles({
      gridArea: [breakpointVariants.gridArea, gridAreaVars, gridArea],
      gridColumnStart: [breakpointVariants.gridColumnStart, gridColumnStartVars, gridColumnStart],
      gridColumnEnd: [breakpointVariants.gridColumnEnd, gridColumnEndVars, gridColumnEnd],
      gridColumn: [breakpointVariants.gridColumn, gridColumnVars, gridColumn],
      gridRowStart: [breakpointVariants.gridRowStart, gridRowStartVars, gridRowStart],
      gridRowEnd: [breakpointVariants.gridRowEnd, gridRowEndVars, gridRowEnd],
      gridRow: [breakpointVariants.gridRow, gridRowVars, gridRow],
      order: [breakpointVariants.order, orderVars, order],
      alignSelf: [breakpointVariants.alignSelf, alignSelfVars, alignSelf],
      justifySelf: [breakpointVariants.justifySelf, justifySelfVars, justifySelf],
    });

    const classNames = getClassNames(sparkyClassName, ...dynamicCssClasses, className);

    return createElement(as, { className: classNames, style: cssVars, ref }, children);
  },
);

GridItem.toString = () => `.${sparkyClassName}`;
GridItem.displayName = 'Grid.Item';

type GridItemProps = {
  /** This is the [native grid-area CSS property](https://developer.mozilla.org/en-US/docs/Web/CSS/grid-area) that will be directly passed on to the grid item.   */
  gridArea?: DynamicVariantProp<'gridArea'>;

  /** This is the [native grid-column-start CSS property](https://developer.mozilla.org/en-US/docs/Web/CSS/grid-column-start) that will be directly passed on to the grid item and correlates to the `PageGrid` column count. */
  gridColumnStart?: DynamicVariantProp<'gridColumnStart'>;

  /** This is the [native grid-column-end CSS property](https://developer.mozilla.org/en-US/docs/Web/CSS/grid-column-end) that will be directly passed on to the grid item. */
  gridColumnEnd?: DynamicVariantProp<'gridColumnEnd'>;

  /** This is the [native grid-column CSS property](https://developer.mozilla.org/en-US/docs/Web/CSS/grid-column) that will be directly passed on to the grid item. */
  gridColumn?: DynamicVariantProp<'gridColumn'>;

  /** This is the [native grid-row-start CSS property](https://developer.mozilla.org/en-US/docs/Web/CSS/grid-row-start) that will be directly passed on to the grid item.   */
  gridRowStart?: DynamicVariantProp<'gridRowStart'>;

  /** This is the [native grid-row-end CSS property](https://developer.mozilla.org/en-US/docs/Web/CSS/grid-row-end) that will be directly passed on to the grid item.   */
  gridRowEnd?: DynamicVariantProp<'gridRowEnd'>;

  /** This is the [native grid-row CSS property](https://developer.mozilla.org/en-US/docs/Web/CSS/grid-row) that will be directly passed on to the grid item.   */
  gridRow?: DynamicVariantProp<'gridRow'>;

  /** This is the [native order CSS property](https://developer.mozilla.org/en-US/docs/Web/CSS/order) that will be directly passed on to the grid item.   */
  order?: DynamicVariantProp<'order'>;

  /** This is the [native align-self CSS property](https://developer.mozilla.org/en-US/docs/Web/CSS/align-self) that will be directly passed on to the grid item. */
  alignSelf?: DynamicVariantProp<'alignSelf'>;

  /** This is the [native justify-self CSS property](https://developer.mozilla.org/en-US/docs/Web/CSS/justify-self) that will be directly passed on to the grid item. */
  justifySelf?: DynamicVariantProp<'justifySelf'>;

  /** The DOM element to render */
  as?: keyof JSX.IntrinsicElements;
  className?: never;
};
